import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { TaskCard } from './TaskCard';
import { STATUS_LABEL, type TaskStatus, type TaskUI } from './types';

const VISIBLE_INITIAL = 5;
const VISIBLE_STEP = 20;

interface TaskColumnProps {
  status: TaskStatus;
  tasks: TaskUI[];
  /** Total pós-filtro (mostrado no header). */
  total: number;
  /** Total absoluto do DB (mostrado em "de N" quando difere do filtrado). */
  absoluteTotal: number;
  variant?: 'primary' | 'issue';
  onTaskClick?: (task: TaskUI) => void;
}

const STATUS_DOT: Record<TaskStatus, string> = {
  pending: 'bg-raios-warning',
  running: 'bg-raios-accent',
  done: 'bg-raios-success',
  failed: 'bg-raios-danger',
  retry: 'bg-raios-warning',
  cancelled: 'bg-raios-muted',
  paused: 'bg-raios-muted',
};

export function TaskColumn({
  status,
  tasks,
  total,
  absoluteTotal,
  variant = 'primary',
  onTaskClick,
}: TaskColumnProps) {
  const [visibleCount, setVisibleCount] = useState(VISIBLE_INITIAL);

  useEffect(() => {
    if (tasks.length !== 0) return;
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) setVisibleCount(VISIBLE_INITIAL);
    });
    return () => {
      cancelled = true;
    };
  }, [tasks.length]);

  const visibleTasks = useMemo(() => tasks.slice(0, visibleCount), [tasks, visibleCount]);
  const hiddenCount = Math.max(0, tasks.length - visibleTasks.length);
  const showAbsoluteHint = total !== absoluteTotal;

  return (
    <section
      className={cn(
        'flex flex-col min-w-[280px] w-[280px] rounded-lg',
        variant === 'primary' ? 'bg-raios-surface/40 border border-raios-elevated/30' : 'bg-transparent',
      )}
      aria-label={`Coluna ${STATUS_LABEL[status]}`}
    >
      <header className="flex items-center justify-between px-3 py-2 border-b border-raios-elevated/30">
        <div className="flex items-center gap-2 min-w-0">
          <span className={cn('h-2 w-2 rounded-full shrink-0', STATUS_DOT[status])} aria-hidden />
          <h2 className="text-xs font-semibold text-raios-text uppercase tracking-wide truncate">
            {STATUS_LABEL[status]}
          </h2>
        </div>
        <div className="flex items-center gap-1 text-[11px] text-raios-text-secondary shrink-0">
          <span className="tabular-nums font-medium text-raios-text">{total}</span>
          {showAbsoluteHint && (
            <span
              className="text-raios-muted tabular-nums"
              title={`${total} bate o filtro de ${absoluteTotal} totais nesta coluna`}
            >
              · de {absoluteTotal}
            </span>
          )}
        </div>
      </header>

      <div className="flex-1 flex flex-col gap-2 p-2 overflow-y-auto min-h-[120px]">
        {tasks.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-[11px] text-raios-muted py-6">
            {absoluteTotal === 0 ? '—' : 'Sem tasks pelo filtro'}
          </div>
        ) : (
          visibleTasks.map((task) => <TaskCard key={task.id} task={task} onClick={onTaskClick} />)
        )}

        {hiddenCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setVisibleCount((v) => v + VISIBLE_STEP)}
            className="mt-1 text-[11px] text-raios-text-secondary hover:text-raios-text justify-center w-full"
          >
            Carregar mais ({hiddenCount})
          </Button>
        )}
      </div>
    </section>
  );
}
